import React, { createContext, useState, useContext, useEffect } from "react";
import { calculateDate } from "libs/dateCalculator";
import { estadosSolicitud } from "components/business/estadoSolicitudDropdown/EstadoSolicitudDropdown";
import { Provincia } from "components/business/provinciaSelector/getProvincias";

interface FiltrosContextType {
  search: string;
  setSearch: (value: string) => void;
  fechaDesde: Date;
  fechaHasta: Date;
  estadoSolicitud: string;
  fechaEstado: string;
  provincia: Provincia | null;
  setProvincia: (provincia: Provincia | null) => void;
  currentPage: number;
  rowsPerPage: number;
  setFechaDesde: (date: Date) => void;
  setFechaHasta: (date: Date) => void;
  setEstadoSolicitud: (estado: string) => void;
  setFechaEstado: (fecha: string) => void;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  handleChangeInterval: (value: number | null) => void;
  handleChangeStartDate: (date: string) => void;
  handleChangeEndDate: (date: string) => void;
  onPageChange: (e: any, n: number) => void;
  onRowsPerPageChange: (e: any) => void;
  ITEMS_PER_PAGE: number[];
}

const FiltrosContext = createContext<FiltrosContextType | undefined>(undefined);

export const FiltrosProvider: React.FC = ({ children }) => {
  const [search, setSearch] = useState("");

  // State for start and end dates
  const [fechaDesde, setFechaDesde] = useState(calculateDate(new Date(), -30));
  const [fechaHasta, setFechaHasta] = useState(new Date());

  // State for estadoSolicitud and fechaEstado
  const [estadoSolicitud, setEstadoSolicitud] = useState(
    estadosSolicitud.Consumo[0].value
  );
  const [fechaEstado, setFechaEstado] = useState(
    estadosSolicitud.ConsumoYen[0].value
  );

  // State for provincia filter
  const [provincia, setProvincia] = useState<Provincia | null>(null);

  // State for pagination
  const ITEMS_PER_PAGE = [10, 25, 50, 100];
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE[0]);

  // Reset currentPage when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [estadoSolicitud, fechaDesde, fechaHasta, fechaEstado, provincia]);

  useEffect(() => {
    if (search === "") {
      setFechaDesde(calculateDate(new Date(), -30));
    } else {
      setFechaDesde(new Date("2024-08-02"));
      setFechaHasta(new Date());
    }
  }, [search]);

  // Handle date changes
  const handleChangeInterval = (value: number | null) => {
    setFechaDesde(calculateDate(new Date(), value));
    setFechaHasta(new Date());
  };

  const handleChangeStartDate = (date: string) => {
    setFechaDesde(date ? new Date(date) : calculateDate(new Date(), -30));
  };

  const handleChangeEndDate = (date: string) => {
    setFechaHasta(date ? new Date(date) : new Date());
  };

  // Handle pagination changes
  const onPageChange = (e: any, n: number) => {
    setCurrentPage(n);
  };

  const onRowsPerPageChange = (e: any) => {
    setRowsPerPage(e.target.value);
  };

  return (
    <FiltrosContext.Provider
      value={{
        search,
        setSearch,
        fechaDesde,
        fechaHasta,
        estadoSolicitud,
        fechaEstado,
        provincia,
        setProvincia,
        currentPage,
        rowsPerPage,
        setFechaDesde,
        setFechaHasta,
        setEstadoSolicitud,
        setFechaEstado,
        setCurrentPage,
        setRowsPerPage,
        handleChangeInterval,
        handleChangeStartDate,
        handleChangeEndDate,
        onPageChange,
        onRowsPerPageChange,
        ITEMS_PER_PAGE,
      }}
    >
      {children}
    </FiltrosContext.Provider>
  );
};

export const useFiltrosContext = () => {
  const context = useContext(FiltrosContext);
  if (!context) {
    throw new Error("useFiltrosContext must be used within a FiltrosProvider");
  }
  return context;
};
